import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { thunkPushEvent } from '../events/actions';
import personIcon from './person.svg';
import phoneIcon from './phone.svg';
import star from './star.svg';

import { Divider } from 'antd';
import { OrderStatus } from '../tracking/entities';
import { EventType } from '../events/entities';
import { withTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

type DriverDetailesProps = {
    className?: string;
    // ClassName bypass is needed for correct working of outside CSS positioning
    name?: string | null;
    phoneNumber?: string | null;
    photoUrl?: string | null;
    rating?: number | null;
    ratingCount?: number | null;
    car?: CarDetailes | null;
    fleetName?: string | null;
    divisionName?: string | null;
    orderStatus?: OrderStatus | null;
    rideStartPinCode?: string | null;
    thunkPushEvent: any;
    isShareRide?: boolean;
    t: TFunction;
};

interface CarDetailes {
    plateNumber?: number | null;
    type?: string | null;
    model?: string | null;
    color?: string | null;
}

const DetailesContainer = styled.div`
    padding: 18px 25px 12px 25px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    font-family: 'Graphik LCG', sans-serif;
`;

const DriverContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const DriverWithAvatar = styled.div`
    display: flex;
    flex-direction: row;
    flex-grow: 1;
`;

const DriverNamePhoneContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 20px;
    flex-grow: 1;
`;

const DriverCarContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

const DriverNameRatingContainer = styled.div`
    font-family: 'Graphik LCG', sans-serif;
    font-size: 14px;
    color: #263238;
    display: flex;
    flex-direction: row;
`;

const PhoneNumberContainer = styled.div`
    /*! @noflip */
    direction: ltr;
    text-align: left;
    font-family: 'Graphik LCG', sans-serif;
    font-size: 12px;
    color: #263238;
`;

const DivisionContainer = styled.div`
    display: flex;
    flex-direction: column;
`;
const DivisionName = styled.div`
    font-weight: 500;
`;

const CarText = styled.div`
    display: flex;
    flex-direction: row;
    font-family: 'Graphik LCG', sans-serif;
    font-size: 12px;
    color: #263238;
`;

const FullDividerWrapper = styled(Divider)`
    margin: 10px -10px;
    width: auto;
`;

const FleetMessageContainer = styled.div`
    font-family: 'Graphik LCG', sans-serif;
    font-size: 12px;
    color: #93a3ab;
    margin-top: 17px;
`;

const PinCodeContainer = styled.div`
    padding: 4px 8px;
    border-radius: 5px;
    font-size: 12px;
    background-color: #dfe5e7;
    white-space: nowrap;
`;

const PinCodeMessageContainer = styled.div`
    margin-left: 10px;
    font-family: 'Graphik LCG', sans-serif;
    font-size: 12px;
    color: #93a3ab;
`;

const RatingImageWrapper = styled.img`
    margin: 0 4px 0 7px;
`;

const getCarText = (car?: CarDetailes | null) => {
    if (car) {
        let carDetailes = '';
        car.model && (carDetailes += car.model);
        if (carDetailes !== '' && car.plateNumber) {
            return `${car.plateNumber} - ${carDetailes}`;
        }
        return car.plateNumber ? car.plateNumber : carDetailes;
    }
    return null;
};

const DriverDetailesComp = (driver: DriverDetailesProps) => {
    if (!driver) {
        return null;
    }

    const onPhoneClick = (e) => {
        driver.thunkPushEvent('1', EventType.EVENT_TYPE_CALL_TO_DRIVER);
    };

    const { t } = driver;

    const name = driver.name ? driver.name : driver.fleetName;
    const carText = getCarText(driver.car);
    const fleetMessage = !driver.name && driver.fleetName ? t('fleet_driver', { fleet: driver.fleetName }) : null;
    const isPinCodeShown = driver.rideStartPinCode && OrderStatus.ORDER_STATUS_PENDING === driver.orderStatus;

    const isMaskingBroken = true; // Temporary - need to be removed as soon as masking will be back to work

    return (
        <DetailesContainer className={driver.className}>
            {name && (
                <DriverContainer>
                    <DriverWithAvatar>
                        <Icon image={driver.photoUrl} type='avatar' />
                        <DriverNamePhoneContainer>
                            <DriverCarContainer>
                                <DriverNameRatingContainer>
                                    {name}
                                    {driver.rating && (
                                        <div>
                                            <RatingImageWrapper src={star} alt='star' />
                                            {Math.round(driver.rating * 10) / 10}
                                        </div>
                                    )}
                                </DriverNameRatingContainer>
                                {carText && <CarText>{carText}</CarText>}
                            </DriverCarContainer>
                            {driver.phoneNumber && !isMaskingBroken && (
                                <PhoneNumberContainer>{driver.phoneNumber}</PhoneNumberContainer>
                            )}
                        </DriverNamePhoneContainer>
                    </DriverWithAvatar>
                    {!driver.isShareRide && driver.phoneNumber && (
                        <a onClick={onPhoneClick} href={'tel:' + driver.phoneNumber}>
                            <Icon type='phone' />
                        </a>
                    )}
                </DriverContainer>
            )}
            {fleetMessage && <FleetMessageContainer>{fleetMessage}</FleetMessageContainer>}
            {!(driver.name || driver.car) && driver.divisionName && (
                <DivisionContainer>
                    <DivisionName>{driver.divisionName}</DivisionName>
                </DivisionContainer>
            )}
            {isPinCodeShown && (
                <div>
                    <FullDividerWrapper />
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <PinCodeContainer>
                            {t('code')}: {driver.rideStartPinCode}
                        </PinCodeContainer>
                        <PinCodeMessageContainer>{t('code_message')}</PinCodeMessageContainer>
                    </div>
                </div>
            )}
        </DetailesContainer>
    );
};

export const DriverDetailes = withTranslation(['driver', 'status'])(
    connect(null, { thunkPushEvent })(DriverDetailesComp)
);

type IconProps = {
    image?: string | null | undefined;
    type: 'phone' | 'avatar';
};

const IconContainer = styled.div`
    padding: 5px 8px;
    border: 1px solid #dfe5e7;
    border-radius: 50%;
    height: 40px;
    width: 40px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    &.avatar {
        background-color: #dfe5e7;
        align-self: flex-end;
    }
`;

const Icon = (icon: IconProps) => {
    const pic = icon.image ? icon.image : icon.type === 'phone' ? phoneIcon : personIcon;

    return (
        <IconContainer className={icon.type}>
            <img style={{ width: '40px' }} src={pic} alt='Icon' />
        </IconContainer>
    );
};
